import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";

// MUI Components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import bgImage from "assets/images/bg-sign-up-cover.jpeg";
import burceMars from "assets/images/bruce-mars.jpg";
import MDAvatar from "components/MDAvatar";

import axios from "axios";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import safeToast from "utils/safeToast";
import { API_ENDPOINTS, getUploadUrl } from "config/api";
import { encryptText, decryptText } from "config/encryption";

function Basic() {
  const [rememberMe, setRememberMe] = useState(false);
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPasswordOnlyMode, setShowPasswordOnlyMode] = useState(false);
  const [userNameFromSession, setUserNameFromSession] = useState("");
  const [profileImg, setProfileImg] = useState("");
  const navigate = useNavigate();

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  useEffect(() => {
    const sessionData = localStorage.getItem("userSession");
    const sessionLoggedUserDetail = localStorage.getItem("UserLogDetails");

    if (!sessionData) {
      safeToast.warn("No session found. Please verify your roll number.");
      navigate("/BasicAuth");
      return;
    }

    try {
      if (sessionData && sessionLoggedUserDetail) {
        const decryptedUserDetails = decryptText(sessionData);
        const parsedUserDetails = JSON.parse(decryptedUserDetails);
        const usn = parsedUserDetails?.usn || "";

        const decryptedLoggedUserDetails = decryptText(sessionLoggedUserDetail);
        const parsedLoggedUserDetails = JSON.parse(decryptedLoggedUserDetails);
        const lus = parsedLoggedUserDetails?.UserName || "";

        if (usn === lus) {
          safeToast.warn("Already logged in. Redirecting to Dashboard...");
          setTimeout(() => {
            navigate("/Mydetails");
          }, 3000);
        } else {
          safeToast.warn("No record found for the given roll number. Redirecting to registration...");
          setTimeout(() => {
            navigate("/authentication/sign-up");
          }, 3000);
        }
      }

      const decryptedUserDetails = decryptText(sessionData);
      const parsedUserDetails = JSON.parse(decryptedUserDetails);
      const usn = parsedUserDetails?.usn || "";
      const profileImage = parsedUserDetails?.img || ""; // fallback set below

      const imgUrl = profileImage.startsWith("http")
        ? profileImage
        : getUploadUrl(profileImage);
      setProfileImg(imgUrl);
      // alert(`Profile image URL: ${imgUrl}`); // Optional debug

      if (usn && usn !== "NOT -Registered") {
        setUserName(usn);
        setUserNameFromSession(usn);
        setShowPasswordOnlyMode(true);
      } else {
        safeToast.warn("No record found for the given roll number. Redirecting to registration...");
        setTimeout(() => {
          navigate("/authentication/sign-up");
        }, 3000);
      }
    } catch (error) {
      console.error("Session decryption/parsing failed:", error);
      safeToast.error("Invalid session. Please verify your roll number again.");
      navigate("/BasicAuth");
    }
  }, [navigate]);

  const handleSignIn = async () => {
    try {
      const encryptedUsername = encryptText(userName);
      const encryptedPassword = encryptText(password);

      const response = await axios.post(API_ENDPOINTS.AUTH.LOGIN, {
        userName: encryptedUsername,
        password: encryptedPassword,
      });

      const loginResponse = response.data;

      if (loginResponse) {
        safeToast.success("Login successful!");
        localStorage.removeItem("UserLogDetails");
        localStorage.setItem("UserLogDetails", loginResponse);

        setTimeout(() => {
          navigate("/MyDetails");
        }, 1000);
      } else {
        safeToast.error("Invalid login response.");
      }
    } catch (error) {
      console.error("Login failed:", error);
      safeToast.error("Login failed. Please check your credentials.");
    }
  };

  const handleSignUp = () => {
    navigate("/authentication/sign-up");
  };

  return (
    <BasicLayout image={bgImage}>
      <ToastContainer />
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Sign in
          </MDTypography>
        </MDBox>

        <MDBox pt={4} pb={3} px={3}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSignIn();
            }}
          >
            {showPasswordOnlyMode ? (
              <>
                <MDBox display="flex" justifyContent="center" mb={2}>
                  <MDAvatar
                    src={profileImg || burceMars}
                    alt="profile-image"
                    size="xl"
                    shadow="sm"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = burceMars;
                    }}
                  />
                </MDBox>
                <MDTypography variant="h6" textAlign="center" mb={2}>
                  👋 Welcome <strong>{userNameFromSession}</strong>, please enter your password to proceed.
                </MDTypography>
                <MDBox mb={2}>
                  <MDInput
                    type="password"
                    label="Password"
                    fullWidth
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </MDBox>
              </>
            ) : (
              <>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Username"
                    fullWidth
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                  />
                </MDBox>
                <MDBox mb={2}>
                  <MDInput
                    type="password"
                    label="Password"
                    fullWidth
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </MDBox>
              </>
            )}

            <MDBox display="flex" alignItems="center" ml={-1}>
              <Switch checked={rememberMe} onChange={handleSetRememberMe} />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Remember me
              </MDTypography>
            </MDBox>

            <MDBox mt={4} mb={1}>
              <MDButton type="submit" variant="gradient" color="info" fullWidth>
                Sign in
              </MDButton>
            </MDBox>

            {!showPasswordOnlyMode && (
              <MDBox mt={3} mb={1} textAlign="center">
                <MDTypography variant="button" color="text">
                  Don&apos;t have an account?{" "}
                  <MDTypography
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    textGradient
                    onClick={handleSignUp}
                    sx={{ cursor: "pointer" }}
                  >
                    Sign up
                  </MDTypography>
                </MDTypography>
              </MDBox>
            )}
          </form>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
